import { requestGaAdmin } from './auth';

async function readResponse(response) {
  let body;
  try {
    body = JSON.parse(await response.text());
  } catch {
    // Missing routes can return HTML. They are unavailable, never zero metrics.
  }
  if (!response.ok) {
    throw Object.assign(new Error('Growth report request failed'), {
      status: response.status,
      code: body?.code,
    });
  }
  if (body == null) throw new Error('Invalid growth report response');
  return Object.prototype.hasOwnProperty.call(body, 'data') ? body.data : body;
}

export async function getStoreInsights(from, to, platform, accessToken, signal) {
  const params = new URLSearchParams({ from, to, platform });
  const response = await requestGaAdmin(`/admin/analytics/store-insights?${params}`, { method: 'GET', signal }, accessToken);
  const data = await readResponse(response);
  if (!Array.isArray(data?.reports) || !Array.isArray(data?.daily) ||
      !Array.isArray(data?.summary) || !Array.isArray(data?.metric_definitions) ||
      !Array.isArray(data?.breakdowns?.country) || !Array.isArray(data?.breakdowns?.source)) {
    throw new Error('Invalid store insights contract');
  }
  return data;
}

export const LANDING_METRICS = ['landing_sessions', 'store_click_sessions', 'ios_store_click_sessions', 'android_store_click_sessions', 'store_click_rate'];
const metricStatuses = new Set(['ok', 'not_configured', 'not_collected', 'delayed', 'permission_denied', 'unavailable', 'partial', 'unsupported']);
const validMetric = (metric, key) => metric && metricStatuses.has(metric.status) &&
  typeof metric.aggregation_basis === 'string' && (metric.value === null ||
    (typeof metric.value === 'number' && Number.isFinite(metric.value) && metric.value >= 0 &&
      (key === 'store_click_rate' ? metric.value <= 1 : Number.isInteger(metric.value))));
const validLandingMetrics = (row) => row && LANDING_METRICS.every((key) => validMetric(row[key], key));

export const CLICK_LOCATIONS = ['home', 'landing', 'unknown'];
export const CLICK_METRICS = LANDING_METRICS.slice(1, 4);

export async function getLandingInsights(from, to, accessToken, signal, entry = 'all') {
  if (!['all', 'home', 'landing'].includes(entry)) throw new Error('Invalid landing entry');
  const params = new URLSearchParams({ from, to, entry });
  const response = await requestGaAdmin(`/admin/analytics/landing-insights?${params}`, { method: 'GET', signal }, accessToken);
  const data = await readResponse(response);
  if (data?.range?.from !== from || data?.range?.to !== to || data?.source?.platform !== 'WEB' ||
      !metricStatuses.has(data?.source?.status) || !validLandingMetrics(data?.summary) ||
      !Array.isArray(data?.daily) || !data.daily.every((row) => /^\d{4}-\d{2}-\d{2}$/.test(row.date || '') && validLandingMetrics(row)) ||
      !Array.isArray(data?.campaigns) || !data.campaigns.every((row) =>
        ['source', 'medium', 'campaign', 'content'].every((key) => typeof row[key] === 'string') && validLandingMetrics(row))) {
    throw new Error('Invalid landing insights contract');
  }
  if (data.range.entry != null && data.range.entry !== entry) throw new Error('Invalid landing entry echo');
  if (data.click_locations != null && (!Array.isArray(data.click_locations) ||
      new Set(data.click_locations.map((row) => row?.location)).size !== data.click_locations.length ||
      !data.click_locations.every((row) => CLICK_LOCATIONS.includes(row?.location) &&
        CLICK_METRICS.every((key) => validMetric(row[key], key))))) {
    throw new Error('Invalid landing click locations contract');
  }
  // An older API may ignore entry and return all traffic. Never present that as a cohort.
  if (entry !== 'all' && data.range.entry == null) {
    const summary = Object.fromEntries(LANDING_METRICS.map((key) => [key, {
      ...data.summary[key], value: null, status: 'unsupported',
    }]));
    return { ...data, source: { ...data.source, status: 'unsupported' }, summary,
      daily: [], campaigns: [], click_locations: [], entry_filter_supported: false };
  }
  return data;
}

export const TRAFFIC_LOCALES = ['ar', 'de', 'en', 'es', 'fil', 'fr', 'hi', 'id', 'ja', 'ko', 'pt-BR', 'ru', 'tr', 'vi', 'zh-Hans', 'zh-Hant'];
const trafficStatuses = new Set(['ok', 'partial', 'not_collected', 'not_configured', 'unavailable']);
const validPageViews = (metric) => metric && trafficStatuses.has(metric.status) &&
  (metric.value === null || (['ok', 'partial'].includes(metric.status) && Number.isSafeInteger(metric.value) && metric.value >= 0));
const unique = (rows, key) => new Set(rows.map(key)).size === rows.length;

export async function getTrafficInsights(from, to, surface, locale, accessToken, signal) {
  if (!['landing', 'home'].includes(surface) || !['all', ...TRAFFIC_LOCALES].includes(locale)) {
    throw new Error('Invalid traffic scope');
  }
  const params = new URLSearchParams({ from, to, surface, locale });
  const response = await requestGaAdmin(`/admin/analytics/traffic-insights?${params}`, { method: 'GET', signal }, accessToken);
  const data = await readResponse(response);
  const dateValid = (date) => /^\d{4}-\d{2}-\d{2}$/.test(date || '') && date >= from && date <= to &&
    new Date(`${date}T00:00:00Z`).toISOString().slice(0, 10) === date;
  if (data?.range?.from !== from || data?.range?.to !== to || data?.range?.surface !== surface || data?.range?.locale !== locale ||
      data?.source?.provider !== 'first_party_anonymous_counter' || data?.source?.timezone !== 'Asia/Seoul' ||
      !trafficStatuses.has(data?.source?.status) || !validPageViews(data?.summary?.page_views) ||
      !Array.isArray(data?.daily) || data.daily.length > 93 || !unique(data.daily, row => row?.date) ||
      !data.daily.every(row => dateValid(row?.date) && validPageViews(row?.page_views)) ||
      !Array.isArray(data?.languages) || data.languages.length > TRAFFIC_LOCALES.length || !unique(data.languages, row => row?.locale) ||
      !data.languages.every(row => TRAFFIC_LOCALES.includes(row?.locale) && (locale === 'all' || row.locale === locale) && validPageViews(row?.page_views)) ||
      !Array.isArray(data?.campaigns) || data.campaigns.length > 34 ||
      !data.campaigns.every(row => ['source', 'medium', 'campaign', 'content', 'campaign_id'].every(key => typeof row?.[key] === 'string' && row[key].length <= 100) && validPageViews(row?.page_views)) ||
      !unique(data.campaigns, row => JSON.stringify([row.source, row.medium, row.campaign, row.content, row.campaign_id]))) {
    throw new Error('Invalid traffic insights contract');
  }
  return data;
}
